import { Drawer, Modal, Form, Input, Typography } from 'antd';
import TemplateBuilder from './Settings.TemplateBuilder';
import { useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import {
  updateTemplateOverride,
  createTemplateOverride,
} from '../../store/actions/settings';
import { useDispatch } from 'react-redux';
import { CopyOutlined } from '@ant-design/icons';
import { useAuth } from '@frontegg/react';
import { analytics } from 'src/services';
import { customFormValidationRules } from 'src/utils';

export const TemplateEditModal = ({ onClose, encounterPhase, template }) => {
  const { dotPhraseOverridesList, templateList: defaulTemplates } = useSelector(
    (state) => state?.settingsState
  );
  const isDefaultTemplate = !!defaulTemplates.find(
    (tmp) => tmp.template_id === template.template_id
  );
  const [currentTemplate, setCurrentTemplate] = useState({
    ...template,
    name: isDefaultTemplate ? `${template.name} (copy)` : template.name,
  });
  const [newTemplateModal, setNewTemplateModal] = useState(false);
  const dispatch = useDispatch();
  const templateBuilderRef = useRef(null);
  const { user } = useAuth();
  const [newTemplateForm] = Form.useForm();

  const onTemplateChange = (template) => {
    setCurrentTemplate(template);
  };

  const sendAnalyticsEvent = (message, data) => {
    analytics.track(`${message} From ${encounterPhase} Template Edit Modal`, {
      ...data,
      email: user?.email,
    });
  };

  const onSave = () => {
    sendAnalyticsEvent('Clicked Save');
    templateBuilderRef.current?.form
      .validateFields()
      .then(() => {
        return dispatch(
          updateTemplateOverride(
            encounterPhase,
            currentTemplate?.template_overrides_id
              ? currentTemplate?.template_overrides_id
              : currentTemplate?.template_id,
            {
              name: currentTemplate.name,
              description: currentTemplate.description,
              body: currentTemplate.body,
            }
          )
        );
      })
      .then(() => {
        onClose();
      })
      .catch(() => {});
  };

  const onSaveAsNew = () => {
    sendAnalyticsEvent('Clicked Save As New');
    templateBuilderRef.current?.form.validateFields().then(() => {
      setNewTemplateModal(true);
    });
  };

  const createNewTemplate = () => {
    newTemplateForm
      .validateFields()
      .then((values) => {
        return dispatch(
          createTemplateOverride(encounterPhase, {
            name: values.name,
            description: values.description,
            body: currentTemplate.body,
            prompt_screen: template?.prompt_screen || 'ambient_summary',
            restrict_verbosities: template?.restrict_verbosities || [],
          })
        );
      })
      .then(() => {
        setNewTemplateModal(false);
        onClose();
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (newTemplateModal) {
      newTemplateForm.setFieldsValue({
        name: currentTemplate.name,
        description: currentTemplate.description,
      });
    }
  }, [newTemplateModal, newTemplateForm]);

  return (
    <Drawer
      width={640}
      footer={
        <div className="flex gap-4">
          <button className="ih-button-secondary left-0" onClick={onClose}>
            Cancel
          </button>
          <button className="ih-button-primary gap-2" onClick={onSaveAsNew}>
            <CopyOutlined />
            Save as new
          </button>
          {!isDefaultTemplate && (
            <button className="ih-button-primary" onClick={onSave}>
              Update
            </button>
          )}
        </div>
      }
      title={isDefaultTemplate ? 'Customize Template' : 'Update Template'}
      open
      onClose={onClose}
    >
      <TemplateBuilder
        ref={templateBuilderRef}
        template={currentTemplate}
        onChange={onTemplateChange}
        dotPhrases={dotPhraseOverridesList}
      />
      {newTemplateModal && (
        <Modal
          title={
            <div className="flex flex-col mb-4">
              <Typography.Text>Create new template</Typography.Text>
              <Typography.Text
                style={{ color: '#808080', fontWeight: '400' }}
                className="text-sm"
              >
                We will create a copy of current template with following
                details. You can select it from the list of templates.
              </Typography.Text>
            </div>
          }
          open
          centered={true}
          footer={
            <div className="flex justify-between">
              <button
                className="ih-button-secondary left-0 mr-auto"
                onClick={() => setNewTemplateModal(false)}
              >
                Cancel
              </button>

              <button className="ih-button-primary" onClick={createNewTemplate}>
                Create
              </button>
            </div>
          }
          className="settings-modal"
        >
          <Form
            className="font-sans"
            layout="vertical"
            form={newTemplateForm}
            initialValues={{
              name: currentTemplate.name,
              description: currentTemplate.description,
            }}
          >
            <Form.Item
              name="name"
              label={
                <p className="font-sans text-md font-medium text-[#000000]">
                  Name
                </p>
              }
              rules={[
                {
                  required: true,
                  message: 'Please input the template name',
                },
                customFormValidationRules.noLeadingOrTrailingSpaces({
                  message:
                    'Template name cannot have leading or trailing spaces',
                }),
              ]}
            >
              <Input className="font-sans" />
            </Form.Item>
            <Form.Item
              name="description"
              label={
                <p className="font-sans text-md font-medium text-[#000000]">
                  Description
                </p>
              }
              rules={[
                {
                  required: true,
                  message: 'Please input the template description',
                },
              ]}
            >
              <Input.TextArea rows={4} className="font-sans" />
            </Form.Item>
          </Form>
        </Modal>
      )}
    </Drawer>
  );
};
